// The hero captivity intents: what a captor decides about the hero they
// hold, and the ransom the captive's house may pay. Each answers whether the
// sim took it.

import type { createRustSimAdapter } from '@aod/sim-adapter';
import { type RefObject, useMemo } from 'react';
import type { CaptureChoice } from './applyIntent.js';

export interface HeroCaptivityActions {
  /** Decide what happens to a hero you hold. */
  readonly chooseHeroCaptivity: (unitId: string, choice: CaptureChoice) => boolean;
  /** Buy a hero of your house back. */
  readonly payHeroRansom: (unitId: string) => boolean;
}

/** What a sim without the module (or before it boots) offers: nothing happens. */
export const NO_HERO_CAPTIVITY: HeroCaptivityActions = {
  chooseHeroCaptivity: () => false,
  payHeroRansom: () => false,
};

export function useHeroCaptivityIntents(
  adapterRef: RefObject<ReturnType<typeof createRustSimAdapter> | null>,
  localPlayerIndex: number,
): HeroCaptivityActions {
  return useMemo(() => {
    const send = (intent: Record<string, unknown>): boolean => {
      const adapter = adapterRef.current;
      if (!adapter) return false;
      return adapter.intent({ ...intent, playerIndex: localPlayerIndex } as never).ok;
    };
    return {
      chooseHeroCaptivity: (unitId, choice) =>
        send({ kind: 'choose_hero_captivity', unitId, choice }),
      payHeroRansom: (unitId) => send({ kind: 'pay_hero_ransom', unitId }),
    };
  }, [adapterRef, localPlayerIndex]);
}
